import { Component, OnInit } from "@angular/core";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { ProgressProduct } from "@common/ADAPT.Common.Model/organisation/progress-product";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { ProgressService } from "@common/progress/progress.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseDialogComponent, DialogResolveData } from "@common/ux/adapt-common-dialog/base-dialog.component/base-dialog.component";
import { catchError, EMPTY, forkJoin, lastValueFrom, tap } from "rxjs";
import { map, switchMap } from "rxjs/operators";

@Component({
    selector: "adapt-add-progress-to-organisation-dialog",
    standalone: false,
    templateUrl: "./add-progress-to-organisation-dialog.component.html",
})
export class AddProgressToOrganisationDialogComponent extends BaseDialogComponent<void> implements OnInit {
    public readonly dialogName = "AddProgressToOrganisation";

    public progressProductTemplates: ProgressProduct[] = [];

    public organisation?: Organisation;
    public productsToAdd: number[] = [];

    public constructor(
        private commonDataService: CommonDataService,
        private dialogService: AdaptCommonDialogService,
        private progressService: ProgressService,
    ) {
        super(DialogResolveData.NotRequired);
    }

    public async ngOnInit() {
        this.progressProductTemplates = await lastValueFrom(this.progressService.getProgressProducts());
    }

    @Autobind
    public saveAndClose() {
        if (!this.organisation) {
            return EMPTY;
        }

        const getProductEntities = (product: ProgressProduct) => ([
            product,
            ...product.progressCategories,
            ...product.progressCategories.flatMap((category) => category.progressItems),
        ]);

        let clonedEntities: IBreezeEntity[] = [];
        return this.progressService.getProgressProducts(this.organisation.organisationId).pipe(
            switchMap((organisationProducts) => {
                const startingOrdinal = organisationProducts.reduce((max, product) => Math.max(max, product.ordinal), -1) + 1;
                const products$ = this.productsToAdd
                    .map((id) => this.progressProductTemplates.find((p) => p.progressProductId === id))
                    .filter((p) => !!p)
                    .sort(SortUtilities.getSortByFieldFunction<ProgressProduct>("ordinal"))
                    .map((p, i) => this.progressService.cloneProgressProduct(p, this.organisation!, startingOrdinal + i));
                return forkJoin(products$);
            }),
            switchMap((products) => {
                clonedEntities = products.flatMap((p) => getProductEntities(p));
                return this.commonDataService.saveEntities(clonedEntities).pipe(
                    map(() => products),
                );
            }),
            switchMap((products) => {
                const productList = products.map((p) => `<li>${p.name} (code ${p.code})</li>`).join("\n");
                return this.dialogService.showMessageDialog(
                    "Products added to organisation",
                    `The following products were added to organisation "${this.organisation!.name}": <ul>${productList}</ul>`,
                    "OK");
            }),
            tap(() => this.resolve()),
            catchError((err) => {
                this.setErrorMessage(ErrorHandlingUtilities.getHttpResponseMessage(err));
                return this.commonDataService.rejectChanges(clonedEntities).pipe(
                    switchMap(() => EMPTY),
                );
            }),
        );
    }
}
